/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import withReducer from '@Hooks/withReducer';

import { settings as heatmapSettingsSelector } from '@Pages/Heatmap/selectors';

import GeneralDialogLayout from '@Containers/Dialogs/GeneralDialogLayout';
import HeatmapSettingsDialogHeader from '@Pages/Heatmap/HeatmapSettingsDialog/components/HeatmapSettingsDialogHeader';
import HeatmapSettingsDialogFooter from '@Pages/Heatmap/HeatmapSettingsDialog/components/HeatmapSettingsDialogFooter';
import HeatmapSettingsDialogContent from '@Pages/Heatmap/HeatmapSettingsDialog/components/HeatmapSettingsDialogContent';

import { REDUCER_KEY_HEATMAP_SETTINGS_DIALOG } from './constants';
import reducer from './reducer';
import { isLoading as isLoadingSelector, settingsResponse as settingsResponseSelector } from './selectors';
import { fetchSettings as fetchSettingsAction, applySettings as applySettingsAction } from './actions';
import { generateCurrentSettings } from './utils';

import './HeatmapSettingsDialog.scss';
import { withCombinedRouteParams } from '../../../AppRoutes/ConnectedRoute';

export function HeatmapSettingsContainer({
  // From DialogsList
  closed,
  selfClose,

  // Heatmap settings
  heatmapSettings,

  // From Redux
  settingsResponse,
  fetchSettings,
  isLoading,
  applySettings,
}) {
  const [currentSettings, setCurrentSettings] = useState(generateCurrentSettings(settingsResponse, heatmapSettings));

  // Fetch settings options and defaults
  useEffect(() => {
    if (!settingsResponse) {
      fetchSettings();
    } else {
      setCurrentSettings(generateCurrentSettings(settingsResponse, heatmapSettings));
    }
  }, [settingsResponse]);

  const updateCurrentSetting = (key, value) => {
    setCurrentSettings({
      ...currentSettings,
      [key]: value,
    });
  };

  return (
    <GeneralDialogLayout
      closed={closed}
      selfClose={selfClose}
      className="heatmap-settings-dialog"
      size="md"
      movable
      header={<HeatmapSettingsDialogHeader />}
      footer={
        <HeatmapSettingsDialogFooter
          disabled={isLoading}
          alwaysShowSettings={currentSettings.showPopup}
          setAlwaysShowSettings={(e, value) => updateCurrentSetting('showPopup', value)}
          applySettings={() => applySettings(currentSettings)}
        />
      }>
      <HeatmapSettingsDialogContent
        isLoading={isLoading}
        settingsResponse={settingsResponse}
        currentSettings={currentSettings}
        updateCurrentSetting={updateCurrentSetting}
      />
    </GeneralDialogLayout>
  );
}

HeatmapSettingsContainer.propTypes = {
  // From DialogsList
  closed: PropTypes.bool,
  selfClose: PropTypes.func.isRequired,

  // Heatmap settings
  heatmapSettings: PropTypes.object,

  // From Redux
  settingsResponse: PropTypes.object,
  fetchSettings: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  applySettings: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  heatmapSettings: heatmapSettingsSelector(state),
  settingsResponse: settingsResponseSelector(state),
  isLoading: isLoadingSelector(state),
});

const mapDispatchToProps = {
  fetchSettings: fetchSettingsAction,
  applySettings: applySettingsAction,
};

const EnhancedHeatmapSettingsContainer = compose(
  withCombinedRouteParams,
  withReducer(REDUCER_KEY_HEATMAP_SETTINGS_DIALOG, reducer),
  connect(mapStateToProps, mapDispatchToProps)
)(HeatmapSettingsContainer);

export default EnhancedHeatmapSettingsContainer;
